/* eslint-disable react/no-unstable-nested-components --
 * CsvDiffTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。
 */
import {
  type SplitSupplyPartnerNoticeImportItem,
  SplitSupplyPartnerNoticeImportKind,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import type { PreviewPartnerSplitSupplyNoticeImportResponse } from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { Alert, Flex, Text } from '@enechain/ecloud-designsystem'

import { CsvDiffTable } from '~/components/common/CsvUpload/CsvDiffTable'
import { HintTooltip } from '~/components/common/HintTooltip'
import { convertAreaLabel } from '~/models/area'
import { protoDateToCivilDate } from '~/models/civilDate'
import {
  ImportErrorsSection,
  ImportIgnoredNote,
  ImportSkippedSection,
  NO_VALUE,
  PointCountCell,
  importPeriodLabel,
} from '~/pages/SplitSupplyPlan/components/ImportResultSections'
import {
  IMPORT_ISSUE_REASON_KEYS,
  IMPORT_KIND_KEYS,
  PARTNER_IMPORT_KEY_PREFIX,
  SKIP_REASON_KEYS,
} from '~/pages/SplitSupplyPlan/models/partnerImport'

type Props = {
  result: PreviewPartnerSplitSupplyNoticeImportResponse
  /**
   * 誤りのあるファイルを外して確認し直す。 外せるファイルが無い (誤りのファイル名が選んだファイルと
   * 一致しない・外すと何も残らない) 時は undefined で、 ボタンを出さない。
   */
  onRemoveBadFiles: (() => void) | undefined
}

const noticeKey = (row: SplitSupplyPartnerNoticeImportItem): string =>
  `${row.section}-${row.targetDate?.year ?? 0}-${row.targetDate?.month ?? 0}-${row.targetDate?.day ?? 0}-${row.areaCode}-${row.counterpartyBusinessCode}`

const PREFIX = PARTNER_IMPORT_KEY_PREFIX

/**
 * 他社の通告値の取込の確認画面 (取込ダイアログの 2 段目)。 backend の preview が返した内容をそのまま見せる:
 * 取り込む通 (新規 / 置き換え・地点・足りない地点)、 取り込まない地点、 無視したファイル、
 * ファイルの誤り。 誤り・取り込める地点が無い・上限超えのどれかがあれば取り込めない (呼び出し側が
 * 「取り込む」 を押せなくする)。
 */
export const PartnerImportConfirmation = ({
  result,
  onRemoveBadFiles,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const monthLabel = (year: number, month: number): string =>
    t('page:splitSupplyPlan.monthLabel', { year, month })

  if (result.tooLarge) {
    return (
      <Alert
        description={t(
          'page:splitSupplyPlan.serverError.reason.partnerNoticeImportTooLarge',
        )}
        status="error"
      />
    )
  }

  const hasErrors = result.errorCount > 0
  return (
    <Flex flexDirection="column" gap={4}>
      {hasErrors && (
        <ImportErrorsSection
          errorCount={result.errorCount}
          errors={result.errors}
          invalidMessage={t(
            'page:splitSupplyPlan.serverError.reason.partnerNoticeImportInvalid',
          )}
          onRemoveBadFiles={onRemoveBadFiles}
          prefix={PREFIX}
          reasonKeys={IMPORT_ISSUE_REASON_KEYS}
        />
      )}

      {!hasErrors && result.nothing && (
        <Alert
          description={t(
            'page:splitSupplyPlan.serverError.reason.partnerNoticeImportNothing',
          )}
          status="warning"
        />
      )}

      {!hasErrors && result.notices.length > 0 && (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="bodyBold">
            {t('page:splitSupplyPlan.partner.import.noticesHeading')}
          </Text>
          <CsvDiffTable
            columns={[
              {
                key: 'period',
                header: t('page:splitSupplyPlan.partner.import.column.period'),
                cell: (row: SplitSupplyPartnerNoticeImportItem) =>
                  importPeriodLabel(
                    row.section,
                    protoDateToCivilDate(row.targetDate),
                    monthLabel,
                  ),
              },
              {
                key: 'area',
                header: t('page:splitSupplyPlan.partner.import.column.area'),
                cell: (row) => convertAreaLabel(row.areaCode),
              },
              {
                key: 'counterparty',
                header: t(
                  'page:splitSupplyPlan.partner.import.column.counterparty',
                ),
                cell: (row) =>
                  row.counterpartyBusinessName === ''
                    ? row.counterpartyBusinessCode
                    : `${row.counterpartyBusinessCode} ${row.counterpartyBusinessName}`,
              },
              {
                key: 'files',
                header: t('page:splitSupplyPlan.partner.import.column.file'),
                // その通に使ったファイル (ZIP の中は中の名前)。 登録に記録するファイル名と同じ並び。
                // 名前は省略せず、 1 ファイル 1 行で全部出す (分けられたファイルは行が並ぶ)
                cell: (row) =>
                  row.sourceFiles.length === 0
                    ? NO_VALUE
                    : row.sourceFiles.map((name, index) => (
                        <Text
                          // 同じ名前のファイルが並びうる (別の ZIP の中等) ので、 並びの位置も key に入れる
                          key={`${index}|${name}`}
                          as="span"
                          display="block"
                          whiteSpace="nowrap"
                        >
                          {name}
                        </Text>
                      )),
              },
              {
                key: 'kind',
                header: t('page:splitSupplyPlan.partner.import.column.kind'),
                cell: (row) => {
                  const key = IMPORT_KIND_KEYS[row.kind]
                  return key === undefined ? NO_VALUE : t(key)
                },
              },
              {
                key: 'points',
                header: t('page:splitSupplyPlan.partner.import.column.points'),
                align: 'right',
                cell: (row) =>
                  t('page:splitSupplyPlan.pointCount', {
                    count: row.pointCount,
                  }),
              },
              {
                key: 'missing',
                header: (
                  <HintTooltip
                    content={t(
                      'page:splitSupplyPlan.partner.import.columnHint.missing',
                    )}
                  >
                    <span>
                      {t('page:splitSupplyPlan.partner.import.column.missing')}
                    </span>
                  </HintTooltip>
                ),
                align: 'right',
                cell: (row) => (
                  <PointCountCell
                    count={row.missingContractPointCount}
                    points={row.missingContractPoints}
                  />
                ),
              },
              {
                key: 'note',
                header: t('page:splitSupplyPlan.partner.import.column.note'),
                cell: (row) =>
                  row.past ? (
                    <HintTooltip
                      content={t(
                        'page:splitSupplyPlan.partner.import.pastHint',
                      )}
                    >
                      <span>
                        {t('page:splitSupplyPlan.partner.import.past')}
                      </span>
                    </HintTooltip>
                  ) : null,
              },
            ]}
            getRowKey={noticeKey}
            getRowTone={(row) =>
              row.kind === SplitSupplyPartnerNoticeImportKind.REPLACE
                ? 'changed'
                : 'new'
            }
            rows={result.notices}
          />
        </Flex>
      )}

      {!hasErrors && result.skippedPointCount > 0 && (
        <ImportSkippedSection
          prefix={PREFIX}
          reasonKeys={SKIP_REASON_KEYS}
          skippedCounts={result.skippedCounts}
          skippedPointCount={result.skippedPointCount}
          skippedPoints={result.skippedPoints}
        />
      )}

      <ImportIgnoredNote count={result.ignoredFileCount} prefix={PREFIX} />
    </Flex>
  )
}
